/**
 * Common helper functions for string, date, file, and data manipulation
 * Includes validation and utility functions used throughout the application
 */

import { logger } from './logger';

// ============================================================================
// String Helpers
// ============================================================================

/**
 * Truncate a string to a maximum length with ellipsis
 * @param str The string to truncate
 * @param maxLength Maximum length of the result
 * @returns Truncated string with ellipsis if needed
 */
export function truncate(str: string, maxLength: number): string {
  logger.debug(`truncate: input="${str}", maxLength=${maxLength}`);

  if (!str) {
    logger.debug('truncate: empty string, returning empty string');
    return '';
  }

  if (str.length <= maxLength) {
    return str;
  }

  const result = `${str.substring(0, Math.max(maxLength - 3, 0))}...`;
  logger.debug(`truncate: result="${result}"`);
  return result;
}

/**
 * Capitalize the first letter of a string
 * @param str The string to capitalize
 * @returns Capitalized string
 */
export function capitalize(str: string): string {
  logger.debug(`capitalize: input="${str}"`);

  if (!str) {
    logger.debug('capitalize: empty string, returning empty string');
    return '';
  }

  const result = str.charAt(0).toUpperCase() + str.slice(1);
  logger.debug(`capitalize: result="${result}"`);
  return result;
}

/**
 * Convert a string to URL-friendly slug format
 * Removes special characters, converts to lowercase, replaces spaces with hyphens
 * @param str The string to slugify
 * @returns Slug version of the string
 */
export function slugify(str: string): string {
  logger.debug(`slugify: input="${str}"`);

  if (!str) {
    logger.debug('slugify: empty string, returning empty string');
    return '';
  }

  const result = str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

  logger.debug(`slugify: result="${result}"`);
  return result;
}

// ============================================================================
// Date Helpers
// ============================================================================

/**
 * Format a date string or Date object to a readable format
 * @param date The date to format
 * @param format Format style: 'short' | 'long' | 'relative'
 * @returns Formatted date string
 */
export function formatDate(date: string | Date, format: 'short' | 'long' | 'relative' = 'short'): string {
  logger.debug(`formatDate: input=${date}, format=${format}`);

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      logger.warn(`formatDate: invalid date provided`);
      return 'Invalid Date';
    }

    if (format === 'short') {
      const result = dateObj.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      logger.debug(`formatDate: short format result="${result}"`);
      return result;
    }

    if (format === 'long') {
      const result = dateObj.toLocaleDateString('en-ZA', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      logger.debug(`formatDate: long format result="${result}"`);
      return result;
    }

    if (format === 'relative') {
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      let result: string;
      if (diffSecs < 60) {
        result = 'just now';
      } else if (diffMins < 60) {
        result = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        result = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffDays < 7) {
        result = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else {
        result = dateObj.toLocaleDateString('en-ZA');
      }
      logger.debug(`formatDate: relative format result="${result}"`);
      return result;
    }

    return dateObj.toString();
  } catch (error) {
    logger.error(`formatDate: error formatting date`, error);
    return 'Error formatting date';
  }
}

/**
 * Check if a date is within a certain threshold of the current time
 * @param date The date to check
 * @param thresholdMs Threshold in milliseconds (default: 24 hours)
 * @returns True if date is within threshold
 */
export function isRecent(date: string | Date, thresholdMs: number = 24 * 60 * 60 * 1000): boolean {
  logger.debug(`isRecent: date=${date}, thresholdMs=${thresholdMs}`);

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      logger.warn(`isRecent: invalid date provided`);
      return false;
    }

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const result = diffMs >= 0 && diffMs < thresholdMs;

    logger.debug(`isRecent: result=${result}, diffMs=${diffMs}`);
    return result;
  } catch (error) {
    logger.error(`isRecent: error checking if date is recent`, error);
    return false;
  }
}

// ============================================================================
// File Helpers
// ============================================================================

/**
 * Allowed image MIME types
 */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;

/**
 * Maximum file size in bytes (10 MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Format file size in bytes to human-readable string
 * @param bytes Number of bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  logger.debug(`formatFileSize: bytes=${bytes}`);

  if (bytes === 0) {
    logger.debug(`formatFileSize: result="0 Bytes"`);
    return '0 Bytes';
  }

  if (!Number.isFinite(bytes) || bytes < 0) {
    logger.warn(`formatFileSize: invalid byte value=${bytes}`);
    return 'Invalid size';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const result = `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;

  logger.debug(`formatFileSize: result="${result}"`);
  return result;
}

/**
 * Extract file extension from filename
 * @param filename The filename to extract extension from
 * @returns File extension (lowercase) without the dot
 */
export function getFileExtension(filename: string): string {
  logger.debug(`getFileExtension: filename="${filename}"`);

  if (!filename) {
    logger.debug(`getFileExtension: empty filename, returning empty string`);
    return '';
  }

  const parts = filename.split('.');
  const result = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';

  logger.debug(`getFileExtension: result="${result}"`);
  return result;
}

/**
 * Check if a MIME type is a valid image type
 * @param contentType The MIME type to validate
 * @returns True if the content type is an allowed image type
 */
export function isValidImageType(contentType: string): boolean {
  logger.debug(`isValidImageType: contentType="${contentType}"`);

  const result = ALLOWED_IMAGE_TYPES.includes(contentType as typeof ALLOWED_IMAGE_TYPES[number]);
  logger.debug(`isValidImageType: result=${result}`);
  return result;
}

// ============================================================================
// ID Helpers
// ============================================================================

/**
 * Generate a UUID v4 string
 * @returns Random UUID v4 string
 */
export function generateId(): string {
  logger.debug(`generateId: generating new UUID v4`);

  const result = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  logger.debug(`generateId: result="${result}"`);
  return result;
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate an email address format
 * @param email Email address to validate
 * @returns True if email appears valid
 */
export function isValidEmail(email: string): boolean {
  logger.debug(`isValidEmail: email="${email}"`);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const result = emailRegex.test(email);
  logger.debug(`isValidEmail: result=${result}`);
  return result;
}

/**
 * Password requirement error messages
 */
interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate a password against security requirements
 * Requirements: minimum 12 characters, uppercase, lowercase, numbers, and symbols
 * @param password Password to validate
 * @returns Validation result with errors array if invalid
 */
export function validatePassword(password: string): PasswordValidationResult {
  logger.debug(`validatePassword: validating password length=${password?.length ?? 0}`);

  const errors: string[] = [];

  if (!password || password.length < 12) {
    errors.push('Password must be at least 12 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one symbol (!@#$%^&*...)');
  }

  const result: PasswordValidationResult = {
    valid: errors.length === 0,
    errors,
  };

  logger.debug(`validatePassword: valid=${result.valid}, errorCount=${errors.length}`);
  return result;
}

// ============================================================================
// Async Helpers
// ============================================================================

/**
 * Delay execution for a specified number of milliseconds
 * @param ms Number of milliseconds to sleep
 * @returns Promise that resolves after the specified delay
 */
export function sleep(ms: number): Promise<void> {
  logger.debug(`sleep: ms=${ms}`);
  return new Promise((resolve) => {
    setTimeout(() => {
      logger.debug(`sleep: ${ms}ms elapsed`);
      resolve();
    }, ms);
  });
}

/**
 * Retry a function that returns a promise with exponential backoff
 * @param fn Function that returns a promise to retry
 * @param retries Number of retry attempts (default: 3)
 * @param delay Base delay in milliseconds between retries (default: 1000)
 * @returns Promise that resolves with the function result
 */
export async function retry<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> {
  logger.debug(`retry: starting with retries=${retries}, delay=${delay}ms`);

  let lastError: Error | null = null;

  for (let i = 0; i <= retries; i++) {
    try {
      logger.debug(`retry: attempt ${i + 1}/${retries + 1}`);
      const result = await fn();
      logger.debug(`retry: succeeded on attempt ${i + 1}`);
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (i < retries) {
        const backoffDelay = delay * Math.pow(2, i);
        logger.warn(`retry: attempt ${i + 1} failed, retrying in ${backoffDelay}ms`, lastError);
        await sleep(backoffDelay);
      }
    }
  }

  logger.error(`retry: all ${retries + 1} attempts failed`, lastError);
  throw lastError || new Error('Retry failed');
}

// ============================================================================
// Array Helpers
// ============================================================================

/**
 * Split an array into chunks of specified size
 * @param array Array to chunk
 * @param size Size of each chunk
 * @returns Array of chunks
 */
export function chunk<T>(array: T[], size: number): T[][] {
  logger.debug(`chunk: arrayLength=${array.length}, chunkSize=${size}`);

  if (size <= 0) {
    logger.warn(`chunk: invalid chunk size=${size}`);
    return [];
  }

  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  logger.debug(`chunk: created ${chunks.length} chunks`);
  return chunks;
}

/**
 * Remove duplicates from an array based on a property/key
 * @param array Array to deduplicate
 * @param key Property key to check for uniqueness
 * @returns Array with unique items
 */
export function uniqueBy<T>(array: T[], key: keyof T): T[] {
  logger.debug(`uniqueBy: arrayLength=${array.length}, key="${String(key)}"`);

  const seen = new Set<unknown>();
  const unique: T[] = [];

  for (const item of array) {
    const value = item[key];
    if (!seen.has(value)) {
      seen.add(value);
      unique.push(item);
    }
  }

  logger.debug(`uniqueBy: result count=${unique.length}`);
  return unique;
}
